import { useState, useCallback, useEffect } from 'react';
import { safeGetJSON, safeSetJSON } from '@/lib/safeStorage';

interface VoiceModeState {
  isSupported: boolean;
  isEnabled: boolean;
  isSpeaking: boolean;
  voice: SpeechSynthesisVoice | null;
  rate: number;
  pitch: number;
}

const STORAGE_KEY = 'human-rooms-voice-mode';

export const useVoiceMode = () => {
  const [state, setState] = useState<VoiceModeState>({
    isSupported: false,
    isEnabled: false,
    isSpeaking: false,
    voice: null,
    rate: 0.85, // Slightly slower for contemplative feel
    pitch: 0.95,
  });
  
  // Initialize
  useEffect(() => {
    const isSupported = 'speechSynthesis' in window;
    const stored = safeGetJSON<{ isEnabled: boolean; rate: number; pitch: number }>(STORAGE_KEY, {
      isEnabled: false,
      rate: 0.85,
      pitch: 0.95,
    });
    
    setState(prev => ({
      ...prev,
      isSupported,
      isEnabled: stored.isEnabled,
      rate: stored.rate,
      pitch: stored.pitch,
    }));
    
    // Load voices
    if (isSupported) {
      const loadVoices = () => {
        const voices = window.speechSynthesis.getVoices();
        // Prefer calm, neutral voices
        const preferred = voices.find(v => 
          v.name.includes('Samantha') || 
          v.name.includes('Karen') ||
          v.name.includes('Daniel') ||
          v.lang.startsWith('en')
        ) || voices[0];
        
        if (preferred) {
          setState(prev => ({ ...prev, voice: preferred }));
        }
      };
      
      loadVoices();
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }
  }, []);
  
  const toggleEnabled = useCallback(() => {
    setState(prev => {
      const newEnabled = !prev.isEnabled;
      safeSetJSON(STORAGE_KEY, {
        isEnabled: newEnabled,
        rate: prev.rate,
        pitch: prev.pitch,
      });
      return { ...prev, isEnabled: newEnabled };
    });
  }, []);
  
  const speak = useCallback((text: string) => {
    if (!state.isSupported || !state.isEnabled) return;
    
    // Cancel any current speech
    window.speechSynthesis.cancel();
    
    const utterance = new SpeechSynthesisUtterance(text);
    
    if (state.voice) {
      utterance.voice = state.voice;
    }
    
    utterance.rate = state.rate;
    utterance.pitch = state.pitch;
    utterance.volume = 0.8; // Slightly quieter for ambiance
    
    utterance.onstart = () => {
      setState(prev => ({ ...prev, isSpeaking: true }));
    };
    
    utterance.onend = () => {
      setState(prev => ({ ...prev, isSpeaking: false }));
    };
    
    utterance.onerror = () => {
      setState(prev => ({ ...prev, isSpeaking: false }));
    };
    
    window.speechSynthesis.speak(utterance);
  }, [state.isSupported, state.isEnabled, state.voice, state.rate, state.pitch]);
  
  const stop = useCallback(() => {
    if (state.isSupported) {
      window.speechSynthesis.cancel();
      setState(prev => ({ ...prev, isSpeaking: false }));
    }
  }, [state.isSupported]);
  
  const setRate = useCallback((rate: number) => {
    setState(prev => {
      safeSetJSON(STORAGE_KEY, {
        isEnabled: prev.isEnabled,
        rate,
        pitch: prev.pitch,
      });
      return { ...prev, rate };
    });
  }, []);
  
  const setPitch = useCallback((pitch: number) => {
    setState(prev => {
      safeSetJSON(STORAGE_KEY, {
        isEnabled: prev.isEnabled,
        rate: prev.rate,
        pitch,
      });
      return { ...prev, pitch };
    });
  }, []);
  
  return {
    ...state,
    toggleEnabled,
    speak,
    stop,
    setRate,
    setPitch,
  };
};
